import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react';
import WebSocketService from '@/lib/websocket-service';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Capacitor } from '@capacitor/core';

// Create context
const WebSocketContext = createContext<WebSocketService | null>(null);

interface WebSocketProviderProps {
  readonly children: ReactNode;
}

export function WebSocketProvider({ children }: Readonly<WebSocketProviderProps>) {
  const { token } = useAuth();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const webSocketService = WebSocketService.getInstance();
  
  // Initialize WebSocket with auth token
  useEffect(() => {
    if (!token) return;
    
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let reconnectAttempts = 0;
    const MAX_RECONNECT_ATTEMPTS = 5;
    
    // Function to handle connection
    const connectWebSocket = () => {
      setIsConnecting(true);
      try {
        // Initialize the WebSocket connection
        webSocketService.initialize(token);
        
        // Check connection status via the WebSocket readyState
        setTimeout(() => {
          const ws = webSocketService.getConnection();
          if (ws && ws.readyState === WebSocket.OPEN) {
            console.log('[WebSocketProvider] Connection verified as open');
            setIsConnecting(false);
            reconnectAttempts = 0;
          } else {
            console.log('[WebSocketProvider] Connection not opened, will retry');
            handleReconnect();
          }
        }, 3000);
      } catch (error) {
        console.error('[WebSocketProvider] Error initializing WebSocket:', error);
        handleReconnect();
      }
    };
    
    // Function to handle reconnection
    const handleReconnect = () => {
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        console.log('[WebSocketProvider] Max reconnect attempts reached');
        setIsConnecting(false);
        toast({
          title: 'Connection Error',
          description: 'Could not connect to game server. Please reload the app.',
          variant: 'destructive',
          duration: 5000,
        });
        return;
      }
      
      reconnectAttempts++;
      console.log(`[WebSocketProvider] Attempting to reconnect (${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`);
      
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
      
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      
      reconnectTimeout = setTimeout(() => {
        connectWebSocket();
      }, delay);
    };
    
    // Initial connection
    connectWebSocket();
    
    // If running in mobile environment, log the server URL for debugging
    if (Capacitor.isNativePlatform()) {
      const serverUrl = import.meta.env.VITE_SERVER_URL ?? window.location.origin;
      console.log('[WebSocketProvider] Mobile environment detected, using server URL:', serverUrl);
    }
    
    // Cleanup on unmount
    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      webSocketService.close();
    };
  }, [token, toast]);
  
  // Handle cleanup when user navigates away or closes browser
  useEffect(() => {
    const handleBeforeUnload = () => {
      webSocketService.close();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [webSocketService]);
  
  return (
    <WebSocketContext.Provider value={webSocketService}>
      {children}
    </WebSocketContext.Provider>
  );
}

// Custom hook for using WebSocket service
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}